import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Cropper from "react-easy-crop";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Heart, ArrowLeft, EnvelopeSimple, VideoCamera, Image as ImageIcon, TextAa, BookmarkSimple, PencilSimple, GridFour, PlayCircle, Camera } from "@phosphor-icons/react";
import { resolveMedia } from "@/lib/media";
import FeedItem from "@/components/FeedItem";

const ContentIcon = ({ type }) =>
  type === "video" ? <VideoCamera size={16} weight="fill" /> :
  type === "image" ? <ImageIcon size={16} weight="fill" /> :
  <TextAa size={16} weight="bold" />;

/* Crop the picked image to a square JPEG using the pixel crop region
 * returned by react-easy-crop. Output is scaled to a max 512×512 avatar. */
async function cropToSquareBlob(imageSrc, pixelCrop, maxSize = 512) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.crossOrigin = "anonymous";
    img.src = imageSrc;
  });
  const targetSize = Math.min(maxSize, pixelCrop.width);
  const canvas = document.createElement("canvas");
  canvas.width = targetSize;
  canvas.height = targetSize;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, targetSize, targetSize,
  );
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92);
  });
}

/* Avatar uploader — clicking the avatar opens the phone / computer photo
 * picker, then a crop modal (drag to pan, pinch/scroll to zoom) so users
 * can frame a consistent square avatar before upload. Owner-only affordance;
 * on another user's profile we render a plain <img>. */
function AvatarUploader({ user }) {
  const { user: currentUser, refresh: refreshUser } = useAuth();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(0);
  const [rawImage, setRawImage] = useState(null); // dataURL of picked file
  const [zoom, setZoom] = useState(1);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [croppedPixels, setCroppedPixels] = useState(null);
  const isOwner = currentUser && currentUser.user_id === user.user_id;
  const src = user.picture
    ? (user.picture.startsWith("/")
       ? `${process.env.REACT_APP_BACKEND_URL || ""}${user.picture}${user.picture.includes("?") ? "&" : "?"}b=${bust}`
       : user.picture)
    : null;

  const onPick = () => { if (!busy && isOwner) fileRef.current?.click(); };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please pick an image file");
    if (f.size > 8 * 1024 * 1024) return toast.error("Image must be under 8 MB");
    const reader = new FileReader();
    reader.onload = () => {
      setRawImage(reader.result);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
      setCroppedPixels(null);
    };
    reader.readAsDataURL(f);
  };

  const onCropComplete = useCallback((_area, pixels) => {
    setCroppedPixels(pixels);
  }, []);

  const closeCropper = () => {
    if (busy) return;
    setRawImage(null);
    setCroppedPixels(null);
  };

  const saveCropped = async () => {
    if (!rawImage || !croppedPixels) return;
    setBusy(true);
    try {
      const blob = await cropToSquareBlob(rawImage, croppedPixels);
      if (!blob) throw new Error("Could not encode image");
      const fd = new FormData();
      fd.append("file", new File([blob], "avatar.jpg", { type: "image/jpeg" }));
      await api.post("/me/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Profile photo updated");
      setBust(Date.now());
      refreshUser?.();
      setRawImage(null);
      setCroppedPixels(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="avatar-upload-btn"
        onClick={onPick}
        disabled={!isOwner || busy}
        aria-label={isOwner ? "Change profile photo" : "Profile photo"}
        className={`relative rounded-full ${isOwner ? "cursor-pointer group" : "cursor-default"}`}
      >
        {src ? (
          <img
            src={src}
            alt=""
            className="w-28 h-28 md:w-32 md:h-32 rounded-full ring-4 ring-blue-500/30 object-cover"
          />
        ) : (
          <div className="w-28 h-28 md:w-32 md:h-32 rounded-full bg-white/10 grid place-items-center">
            <Camera size={32} weight="fill" className="text-white/40"/>
          </div>
        )}
        {isOwner && (
          <span className="absolute bottom-0 right-0 grid place-items-center w-9 h-9 rounded-full
                            bg-amber-400 text-black shadow-lg ring-2 ring-[#121212]
                            group-hover:scale-105 transition-transform">
            <Camera size={16} weight="fill"/>
          </span>
        )}
        {busy && (
          <span className="absolute inset-0 rounded-full bg-black/60 grid place-items-center
                            text-xs text-white font-bold">
            Uploading…
          </span>
        )}
      </button>
      {isOwner && (
        <input
          type="file"
          accept="image/*"
          ref={fileRef}
          onChange={onFile}
          className="hidden"
          data-testid="avatar-file-input"
        />
      )}

      {/* Crop modal — square 1:1 output */}
      <Dialog open={!!rawImage} onOpenChange={(o) => { if (!o) closeCropper(); }}>
        <DialogContent
          data-testid="avatar-crop-dialog"
          className="max-w-md w-[92vw] bg-[#0a0a0a] border-white/10 text-white p-0 overflow-hidden"
        >
          <DialogHeader className="px-5 pt-5 pb-2">
            <DialogTitle className="font-heading text-lg font-black">
              Crop your profile photo
            </DialogTitle>
            <p className="text-xs text-white/60">
              Drag to reposition · pinch or scroll to zoom
            </p>
          </DialogHeader>
          <div className="relative w-full h-72 bg-black" data-testid="avatar-crop-canvas">
            {rawImage && (
              <Cropper
                image={rawImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>
          <div className="px-5 py-3 flex items-center gap-3 border-t border-white/10">
            <span className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Zoom</span>
            <input
              type="range"
              data-testid="avatar-crop-zoom"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1 accent-amber-400"
            />
          </div>
          <div className="px-5 pb-5 flex gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              className="border-white/20 bg-transparent hover:bg-white/5"
              onClick={closeCropper}
              disabled={busy}
              data-testid="avatar-crop-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={saveCropped}
              disabled={busy || !croppedPixels}
              className="bg-amber-400 hover:bg-amber-500 text-black font-heading font-bold"
              data-testid="avatar-crop-save"
            >
              {busy ? "Uploading…" : "Save photo"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Profile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, refresh: refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePost, setActivePost] = useState(null);
  const [saved, setSaved] = useState([]);
  const [liked, setLiked] = useState([]);
  const [savedLoaded, setSavedLoaded] = useState(false);
  const [likedLoaded, setLikedLoaded] = useState(false);
  const [tab, setTab] = useState("videos");
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editBio, setEditBio] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/users/${userId}/profile`);
      setData(data);
      if (data.is_me) {
        setEditName(data.user.name || "");
        setEditBio(data.user.bio || "");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Profile not found");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // Poll for processing progress while the user has processing videos on their own profile.
  useEffect(() => {
    if (!data?.is_me || !data.posts) return;
    const hasProcessing = data.posts.some((p) =>
      (p.status === "draft" && p.processing_status !== "ready" && p.processing_status !== "failed") ||
      p.processing_status === "processing" ||
      p.processing_status === "trimming" ||
      ["init", "uploading", "queued", "processing"].includes(p.processing_session_status)
    );
    if (!hasProcessing) return;
    const t = setInterval(() => { load(); }, 3000);
    return () => clearInterval(t);
  }, [data, load]);

  useEffect(() => {
    if (!data?.is_me) return;
    if (tab === "saved" && !savedLoaded) {
      api.get("/me/bookmarks").then(({ data }) => setSaved(data)).catch(() => {}).finally(() => setSavedLoaded(true));
    }
    if (tab === "liked" && !likedLoaded) {
      api.get("/me/liked").then(({ data }) => setLiked(data)).catch(() => {}).finally(() => setLikedLoaded(true));
    }
  }, [tab, data?.is_me, savedLoaded, likedLoaded]);

  const toggleFollow = async () => {
    if (!data) return;
    try {
      if (data.is_following) {
        await api.delete(`/users/${userId}/follow`);
        setData({ ...data, is_following: false, followers: Math.max(0, data.followers - 1) });
      } else {
        await api.post(`/users/${userId}/follow`);
        setData({ ...data, is_following: true, followers: data.followers + 1 });
        toast.success(`Following @${data.user.name}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const saveEdits = async () => {
    setEditSaving(true);
    try {
      await api.put("/me/profile", { name: editName.trim(), bio: editBio });
      toast.success("Profile updated");
      setEditOpen(false);
      refreshUser?.();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setEditSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-white/60">Loading…</div>;
  if (!data) return <div className="p-8 text-white/60">User not found.</div>;

  const { user, followers, following, is_following, is_me, posts, total_likes } = data;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6" data-testid="profile-page">
      <button
        data-testid="profile-back"
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-white/70 hover:text-white text-sm"
      >
        <ArrowLeft size={16} weight="bold" /> Back
      </button>

      {/* Header */}
      <Card className="bg-[#121212] border-white/10">
        <CardContent className="p-6 flex flex-col items-center gap-4 text-center">
          <AvatarUploader user={user}/>
          <div>
            <h1 className="font-heading text-3xl font-black">{user.name}</h1>
            {user.bio && (
              <p className="mt-2 text-sm text-white/70 max-w-md whitespace-pre-line">{user.bio}</p>
            )}
          </div>

          <div className="flex justify-center gap-6 mt-1 text-sm">
            <Stat label="Posts" value={posts.length} />
            <Stat label="Followers" value={followers} />
            <Stat label="Following" value={following} />
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-1 text-red-400">
                <Heart size={14} weight="fill" />
                <span className="font-heading font-bold text-white">{total_likes}</span>
              </div>
              <span className="text-white/50 text-xs">Likes</span>
            </div>
          </div>

          <div className="flex gap-2 mt-2 flex-wrap justify-center">
            {is_me ? (
              <>
                <Button
                  data-testid="profile-edit-btn"
                  onClick={() => setEditOpen(true)}
                  className="bg-gradient-to-r from-blue-500 to-emerald-500 text-white gap-2"
                >
                  <PencilSimple size={16} weight="bold" /> Edit Profile
                </Button>
                <Link to="/dashboard">
                  <Button variant="outline" className="border-white/20 bg-transparent hover:bg-white/5">
                    Wallet
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Button
                  data-testid="profile-follow-btn"
                  onClick={toggleFollow}
                  className={
                    is_following
                      ? "bg-white/10 hover:bg-white/15 text-white ring-1 ring-white/20"
                      : "bg-gradient-to-r from-blue-500 to-emerald-500 text-white"
                  }
                >
                  {is_following ? "Following" : "+ Follow"}
                </Button>
                <Button
                  data-testid="profile-message-btn"
                  onClick={() => navigate(`/inbox?with=${user.user_id}`)}
                  variant="outline"
                  className="border-white/20 bg-transparent hover:bg-white/5 gap-2"
                >
                  <EnvelopeSimple size={16} weight="bold" /> Message
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className={`bg-white/5 grid ${is_me ? "grid-cols-3" : "grid-cols-1"}`}>
          <TabsTrigger data-testid="profile-tab-videos" value="videos" className="gap-2">
            <GridFour size={16} weight="bold" /> Videos
          </TabsTrigger>
          {is_me && (
            <>
              <TabsTrigger data-testid="profile-tab-saved" value="saved" className="gap-2">
                <BookmarkSimple size={16} weight="bold" /> Saved
              </TabsTrigger>
              <TabsTrigger data-testid="profile-tab-liked" value="liked" className="gap-2">
                <Heart size={16} weight="bold" /> Liked
              </TabsTrigger>
            </>
          )}
        </TabsList>

        <TabsContent value="videos" className="mt-4">
          <PostsGrid
            posts={posts}
            onOpen={(p) => {
              // Own drafts that finished processing → editor. Still processing → toast.
              if (is_me && p.status === "draft") {
                if (p.processing_status === "ready") {
                  navigate(`/editor/${p.post_id}`);
                } else if (p.processing_status === "failed") {
                  toast.error(p.processing_error || "Processing failed — please re-upload");
                } else {
                  toast.info(`Still processing — ${p.processing_progress || 0}%`);
                }
                return;
              }
              if (is_me && p.status === "pending_payment") {
                navigate(`/pay/${p.post_id}`);
                return;
              }
              setActivePost(p);
            }}
            onDelete={is_me ? async (postId) => {
              if (!window.confirm("Delete this post? It will be removed from your profile.")) return;
              try {
                await api.delete(`/posts/${postId}`);
                toast.success("Post deleted");
                // Re-fetch profile so the grid updates immediately.
                const { data: fresh } = await api.get(`/users/${userId}/profile`);
                setData(fresh);
              } catch (e) {
                toast.error(e.response?.data?.detail || "Failed to delete");
              }
            } : null}
            emptyText="No posts yet."
          />
        </TabsContent>
        {is_me && (
          <>
            <TabsContent value="saved" className="mt-4">
              {!savedLoaded ? (
                <p className="text-white/50 text-sm text-center py-10">Loading…</p>
              ) : (
                <PostsGrid posts={saved} onOpen={(p) => setActivePost(p)} emptyText="You haven't saved any posts yet." />
              )}
            </TabsContent>
            <TabsContent value="liked" className="mt-4">
              {!likedLoaded ? (
                <p className="text-white/50 text-sm text-center py-10">Loading…</p>
              ) : (
                <PostsGrid posts={liked} onOpen={(p) => setActivePost(p)} emptyText="You haven't liked anything yet." />
              )}
            </TabsContent>
          </>
        )}
      </Tabs>

      {/* Post playback modal */}
      {activePost && (
        <div
          data-testid="profile-post-modal"
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setActivePost(null)}
        >
          <div
            className="relative w-full max-w-md h-[85vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <FeedItem
              post={{ ...activePost, author: activePost.author || (tab === "videos" ? user : activePost.author) }}
              onUpdate={load}
            />
            <button
              data-testid="profile-post-close"
              onClick={() => setActivePost(null)}
              className="absolute -top-10 right-0 text-white/70 hover:text-white text-sm"
            >
              Close X
            </button>
          </div>
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 font-bold">Name</label>
              <Input
                data-testid="edit-name-input"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                maxLength={60}
                className="bg-white/5 border-white/10 text-white mt-1"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 font-bold">Bio</label>
              <Textarea
                data-testid="edit-bio-input"
                value={editBio}
                onChange={(e) => setEditBio(e.target.value)}
                maxLength={300}
                rows={3}
                placeholder="Tell people what you're about…"
                className="bg-white/5 border-white/10 text-white mt-1"
              />
              <p className="text-[11px] text-white/40 mt-1 text-right">{editBio.length}/300</p>
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                data-testid="edit-save-btn"
                onClick={saveEdits}
                disabled={editSaving || !editName.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-700 font-heading font-bold"
              >
                {editSaving ? "Saving…" : "Save changes"}
              </Button>
              <Button
                data-testid="edit-cancel-btn"
                variant="outline"
                onClick={() => setEditOpen(false)}
                className="border-white/20 bg-transparent hover:bg-white/5"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex flex-col items-center">
      <span className="font-heading font-black text-lg">{value}</span>
      <span className="text-white/50 text-xs">{label}</span>
    </div>
  );
}

function PostsGrid({ posts, onOpen, emptyText, onRetry, onDelete }) {
  if (!posts || posts.length === 0) {
    return <p className="text-white/50 text-sm text-center py-10">{emptyText}</p>;
  }
  return (
    <div className="grid grid-cols-3 gap-1.5" data-testid="profile-posts-grid">
      {posts.map((p) => {
        const isProcessing = p.processing_status === "processing" || p.processing_status === "trimming" || p.processing_session_status === "processing" || p.processing_session_status === "queued" || p.processing_session_status === "uploading" || p.processing_session_status === "init";
        const isFailed = p.processing_status === "failed" || p.processing_session_status === "failed";
        const progress = typeof p.processing_progress === "number" ? p.processing_progress : 0;
        return (
          <button
            key={p.post_id}
            data-testid={`profile-post-${p.post_id}`}
            onClick={() => onOpen(p)}
            className="relative aspect-[9/16] bg-black overflow-hidden rounded-md group"
          >
            {p.content_type === "video" && (p.thumbnail_url || p.media_url) && (
              <img src={resolveMedia(p.thumbnail_url || p.media_url)} alt="" className={`absolute inset-0 w-full h-full object-cover ${isProcessing ? "opacity-40 blur-sm" : ""}`} />
            )}
            {p.content_type === "video" && !p.thumbnail_url && !p.media_url && (
              <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-black to-emerald-950" />
            )}
            {p.content_type === "image" && p.media_url && (
              <img src={resolveMedia(p.media_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
            )}
            {p.content_type === "text" && (
              <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-black to-emerald-900 flex items-center justify-center p-2">
                <p className="text-[10px] font-heading font-bold text-white text-center line-clamp-5">{p.caption}</p>
              </div>
            )}

            {/* Processing overlay */}
            {isProcessing && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/50"
                data-testid={`profile-post-processing-${p.post_id}`}
              >
                <div className="relative w-10 h-10">
                  <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
                    <circle
                      cx="18"
                      cy="18"
                      r="16"
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="3"
                      strokeDasharray={`${(progress / 100) * 100.5} 100.5`}
                      strokeLinecap="round"
                      className="transition-all duration-500"
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-heading font-black text-white">
                    {progress}%
                  </span>
                </div>
                <span className="text-[9px] uppercase tracking-wider text-blue-200 font-bold">
                  {p.processing_stage || "Processing"}
                </span>
              </div>
            )}
            {isFailed && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-red-900/70 p-2"
                data-testid={`profile-post-failed-${p.post_id}`}
              >
                <span className="text-[10px] uppercase tracking-wider text-red-100 font-black">Failed</span>
                {p.processing_error && (
                  <span className="text-[9px] text-red-100/80 text-center line-clamp-3">{p.processing_error}</span>
                )}
                <div className="mt-1 flex items-center gap-1.5">
                  {onRetry && p.video_upload_id && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); onRetry(p.video_upload_id); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onRetry(p.video_upload_id); } }}
                      className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-[10px] font-heading font-bold text-white cursor-pointer"
                      data-testid={`profile-post-retry-${p.post_id}`}
                    >
                      Retry
                    </span>
                  )}
                  {onDelete && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); onDelete(p.post_id); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onDelete(p.post_id); } }}
                      className="px-2 py-1 rounded bg-red-500/80 hover:bg-red-500 text-[10px] font-heading font-bold text-white cursor-pointer"
                      data-testid={`profile-post-delete-${p.post_id}`}
                    >
                      Delete
                    </span>
                  )}
                </div>
              </div>
            )}
            {p.status && p.status !== "live" && !isProcessing && !isFailed && (
              <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-yellow-500/90 text-black text-[9px] font-heading font-black uppercase">
                {p.status === "draft" ? "Ready to Edit" :
                 p.status === "pending_payment" ? "Pay to Publish" :
                 p.status === "pending_approval" ? "In review" : p.status}
              </div>
            )}

            <div className="absolute top-1 right-1 text-white/80">
              {p.content_type === "video" ? <PlayCircle size={16} weight="fill" /> : <ContentIcon type={p.content_type} />}
            </div>
            {!isProcessing && !isFailed && (
              <div className="absolute bottom-1 left-1 right-1 flex items-center gap-2 text-[10px] text-white/90 font-bold drop-shadow">
                <Heart size={10} weight="fill" /> {p.like_count}
                <span>·</span>
                <span>{p.view_count}v</span>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
